import { useContext, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { PageContext } from '@pages/FormCreateOrEdit'
import { useInputs } from '@pages/FormCreateOrEdit/components/AddFieldDialog/hooks/useInputs'

import {
  useFetchAllFormByObjectCodeQuery,
  useFetchAllObjectQuery,
  useFetchAllViewQuery,
  useFetchObjectByCodeQuery,
} from '@redux/api'

import { usePrompt } from '@hooks'
import { generateDefaultObjectByFields, getEntityTypeForRequest } from '@helpers'
import {
  BIND_TYPE,
  DURATION_UNIT,
  ENTITY,
  FIELD_VALUE_FORMAT,
  FIELD_VALUE_TYPE,
  MODEL_TYPE,
  OBJECT_FIELD_TYPE,
  PREFILL_OPTIONS,
} from '@constants'
import { AutocompleteOption, ObjectFieldDTO } from '@types'

import { EmbeddedObject } from '@gantt/components/GanttCreateOrEdit'

import { FieldForm, getDefaultValue } from '../helpers/getDefaultValue'
import { useHandlers } from '../hooks/useHandlers'

export const useAddFieldDialog = () => {
  const defaultValues: FieldForm = getDefaultValue()

  const methods = useForm<FieldForm>({ defaultValues })
  const [isLoading, setLoading] = useState(true)
  const [isFieldsLoading, setFieldsLoading] = useState(true)
  const [objectField, setObjectField] = useState<ObjectFieldDTO>()

  const {
    reset,
    formState: { isDirty, errors },
    watch,
    clearErrors,
    setValue,
    getValues,
  } = methods

  const watchValue = watch('value')
  const watchBinding = watch('bindType')
  const watchAsDuration = watch('asDuration')
  const watchAsCheckbox = watch('asCheckbox')
  const watchIsAddFieldValue = watch('isAddFieldValues')

  const {
    addField,
    currentRow,
    modalType,
    editField,
    formCode,
    modifyObjectFields,
    objectCode,
    objectFieldsForFilter, // Исходные поля
    bindingValuesFieldRender,
    bindingValuesColumns,
    onCloseModal,
    onSetModalProperty,
    mainTable: { rows: fields },
    toggleOpenScriptValueDialog,
  } = useContext(PageContext)
  usePrompt({ when: isDirty })

  const calculatedEmbeddedObjectCode = objectField?.model ?? ''
  const watchEmbeddedObjectCode = watch('embeddedObjectCode')
  const watchDropDownObjectCode = watch('dropDownObjectCode')
  const watchTargetPathArray = watch('targetPathArray')
  const embeddedObjectCode =
    typeof watchEmbeddedObjectCode === 'object' && watchEmbeddedObjectCode?.id
      ? String(watchEmbeddedObjectCode.id)
      : ''
  const dropDownObjectCode =
    typeof watchDropDownObjectCode === 'object' && watchDropDownObjectCode?.id
      ? String(watchDropDownObjectCode.id)
      : ''
  const targetObjectCode = dropDownObjectCode || calculatedEmbeddedObjectCode

  const code = currentRow?.code || null
  const valueType = objectField?.valueType || null

  const { data: objectForField } = useFetchObjectByCodeQuery(embeddedObjectCode, {
    skip: !embeddedObjectCode,
  })

  const { data: objectForTargetField } = useFetchObjectByCodeQuery(targetObjectCode, {
    skip: !targetObjectCode,
  })

  const defaultTargetField = useMemo(() => {
    return objectForTargetField?.fields?.find(field =>
      valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK ? field.isInternalId : field.isPk
    )
  }, [objectForTargetField, valueType])

  const defaultTargetFieldPath = useMemo<EmbeddedObject[]>(
    () =>
      defaultTargetField && targetObjectCode && objectField?.valueType
        ? [
            {
              objectCode: targetObjectCode,
              field: defaultTargetField.name,
              valueType: defaultTargetField.valueType,
              refValueType: objectField.valueType,
              isPk: defaultTargetField.isPk,
              isInternalId: defaultTargetField.isInternalId,
              description: defaultTargetField.desc || undefined,
              model: defaultTargetField.model,
              type: defaultTargetField.type,
            },
          ]
        : [],
    [defaultTargetField, targetObjectCode, objectField?.valueType]
  )

  const { data: forms } = useFetchAllFormByObjectCodeQuery(objectField?.model, {
    skip: !objectField?.model,
  })

  const { data: dropdownEntities } = useFetchAllViewQuery(
    {
      objectCode: dropDownObjectCode,
      viewType: getEntityTypeForRequest(ENTITY.DROP_DOWN_ENTITY),
    },
    { refetchOnMountOrArgChange: true, skip: !dropDownObjectCode }
  )

  const { data: objects } = useFetchAllObjectQuery()

  const srcObj = generateDefaultObjectByFields(objectFieldsForFilter || [])
  const getObjectCodeOption = (objectCode?: string | null): AutocompleteOption<string> | null => {
    if (!objectCode) {
      return null
    }

    const object = objects?.find(item => item.code === objectCode)

    return object ? { id: object.code, label: object.title } : null
  }

  // TODO дубликат
  useEffect(() => {
    const field = objectFieldsForFilter?.find(field => field.name === watchValue)
    setObjectField(field ? field : undefined)
    clearErrors()

    if (currentRow && field && currentRow?.value === field?.name) {
      setValue('objectValue', currentRow?.objectValue)

      return
    }
  }, [objectFieldsForFilter, watchValue])

  useEffect(() => {
    setValue('embeddedObjectCode', getObjectCodeOption(calculatedEmbeddedObjectCode), {
      shouldDirty: false,
    })

    if (!watchTargetPathArray?.length) {
      setValue('dropDownObjectCode', getObjectCodeOption(calculatedEmbeddedObjectCode), {
        shouldDirty: false,
      })
    }
  }, [calculatedEmbeddedObjectCode, objects, setValue])

  // Когда поля asСheckbox и asDuration скрываются, нужно ресетить их значения, чтобы случайно не отправилось true
  // при иных valueType
  useEffect(() => {
    if (objectField?.type === OBJECT_FIELD_TYPE.ENUM) {
      return
    }

    if (valueType !== FIELD_VALUE_TYPE.INTEGER && valueType !== FIELD_VALUE_TYPE.BOOLEAN) {
      if (watchAsCheckbox && isDirty) {
        setValue('asCheckbox', false)
      }
    }

    if (valueType !== FIELD_VALUE_TYPE.INTEGER && valueType !== FIELD_VALUE_TYPE.DOUBLE) {
      if (watchAsDuration && isDirty) {
        setValue('asDuration', false)
      }
    }

    if (valueType !== FIELD_VALUE_TYPE.STRING) {
      setValue('isMultiline', false)
    }
  }, [valueType])

  useEffect(() => {
    if (currentRow) {
      const field = objectFieldsForFilter?.find(field => field.name === currentRow.value)

      const preFill = currentRow.preFill
      const preFillRootObjectCode = preFill ? preFill?.preFillLink[0].objectCode : null
      const preFillFilterNameObjectCode = preFill?.preFillFilterName?.length
        ? preFill?.preFillFilterName[0].objectCode
        : null

      const preFillRootObject = preFillRootObjectCode
        ? objects?.find(object => object.code === preFillRootObjectCode)
        : null
      const preFillFilterNameObject = preFillFilterNameObjectCode
        ? objects?.find(object => object.code === preFillFilterNameObjectCode)
        : null

      reset({
        ...currentRow,
        valueType: valueType || currentRow.valueType,
        objectFormCode: currentRow?.objectFormCode
          ? {
              id: currentRow?.objectFormCode,
              label: currentRow?.objectFormTitle,
            }
          : null,
        params: currentRow.params ? JSON.stringify(currentRow.params) : '',
        json: Boolean(currentRow.params),
        required: Boolean(currentRow?.userRequired),
        dropDownListCode: currentRow.dropDownList
          ? {
              id: currentRow.dropDownList.code,
              label: currentRow.dropDownList.title,
            }
          : null,
        dropDownWindowCode: currentRow.dropDownWindow
          ? {
              id: currentRow.dropDownWindow.code,
              label: currentRow.dropDownWindow.title,
            }
          : null,
        dropDownListFilters: currentRow.dropDownListFilters || [],
        dropDownWindowFilters: currentRow.dropDownWindowFilters || [],
        placeholderValue: currentRow.placeholderValue || '',
        isDefaultPlaceholder: Boolean(currentRow.isDefaultPlaceholder),
        generalFilterField: currentRow?.generalFilterField ? currentRow?.generalFilterField : '',
        formatDate: currentRow.formatDate
          ? { id: currentRow.formatDate, label: currentRow.formatDate }
          : null,
        isEnabledTimeMenu: currentRow.isEnabledTimeMenu,
        asCheckbox:
          currentRow.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
          currentRow.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN,
        asDuration: currentRow.valueFormat === FIELD_VALUE_FORMAT.TIME,
        durationUnit: currentRow.durationUnit ?? DURATION_UNIT.SECONDS,
        preFillType: preFill
          ? {
              id: preFill.preFillType === PREFILL_OPTIONS.DYNAMIC ? 0 : 1,
              option: preFill.preFillType,
              label: PREFILL_OPTIONS[preFill.preFillType],
            }
          : null,
        preFillSourceObject: preFillRootObject
          ? { id: preFillRootObject.code, label: preFillRootObject.title }
          : null,
        embeddedObjectCode: getObjectCodeOption(currentRow?.embeddedObjectCode),
        dropDownObjectCode: getObjectCodeOption(currentRow?.dropDownObjectCode),
        targetPathArray: currentRow?.targetPathArray || [],
        targetPathArrayValue: currentRow?.targetPathArray?.map(item => item.field).join('.') || '',
        preFillFilterObject: preFillFilterNameObject
          ? { id: preFillFilterNameObject.code, label: preFillFilterNameObject.title }
          : null,
        preFillLink: preFill?.preFillLink,
        preFillFilterName: preFill?.preFillFilterName,
        preFillLinkStr: preFill?.preFillLinkStr,
        preFillFilterNameStr: preFill?.preFillFilterNameStr,
        preFillStaticValue: preFill?.value || '',
        isMultiline: currentRow.isMultiline,
        fieldValues: currentRow.fieldValues,
        isAddFieldValues: currentRow.isAddFieldValues,
        conditionsDisabled: currentRow.conditionsDisabled || '',
      })
      setLoading(false)
      setObjectField(field ? field : undefined)
    }
  }, [currentRow, objects])

  useEffect(() => {
    if (objectField?.required) {
      setValue('required', true)
    }
  }, [objectField])

  useEffect(() => {
    onSetModalProperty?.(MODEL_TYPE.FIELD)
  }, [])

  useEffect(() => {
    if (currentRow?.userRequired && watchBinding === BIND_TYPE.JS) {
      setValue('required', false)
    }
  }, [watchBinding])

  useEffect(() => {
    if (bindingValuesFieldRender) {
      setFieldsLoading(false)
    }
  }, [bindingValuesFieldRender])

  useEffect(() => {
    if (watchValue !== currentRow?.value) {
      setValue('objectFormCode', null)

      if (objectField?.required === false) {
        setValue('required', false)
      }
    }
  }, [watchValue, objectField])

  useEffect(() => {
    if (!objectForTargetField) {
      return
    }

    if (!watchValue || !defaultTargetField || defaultTargetFieldPath.length === 0) {
      if (dropDownObjectCode && !watchTargetPathArray?.length) {
        setValue('targetPathArray', [], { shouldDirty: true })
        setValue('targetPathArrayValue', '', { shouldDirty: true })
      }

      return
    }

    const shouldDirty = watchValue !== currentRow?.value

    if (!watchTargetPathArray?.length) {
      setValue('targetPathArray', defaultTargetFieldPath, { shouldDirty })
      setValue('targetPathArrayValue', defaultTargetField.name, { shouldDirty })
    }
  }, [objectForTargetField])

  useEffect(() => {
    if (watchBinding === BIND_TYPE.JS) {
      const value =
        currentRow?.value && currentRow.bindType === BIND_TYPE.JS ? currentRow.value : null

      setValue('value', value)
    }
    if (watchBinding === BIND_TYPE.FIELD) {
      const value =
        currentRow?.value && currentRow.bindType === BIND_TYPE.FIELD ? currentRow.value : null

      setValue('value', value)
    }
  }, [watchBinding])

  const { handlers, state } = useHandlers({
    addField,
    editField,
    formCode,
    onCloseModal,
    modalType,
    objectField,
    defaultValues,
    currentRow,
    methods,
  })

  const { inputs } = useInputs({
    modifyObjectFields,
    objectFieldsForFilter,
    currentRow,
    dropdownEntities,
    forms,
    embeddedObjectCode,
    fields,
    methods,
    objectForField,
    bindingValuesColumns,
    objectField,
    valueType,
    srcObj,
    toggleOpenScriptValueDialog,
    modalType,
    objects,
    handleChangeBindType: handlers.handleChangeBindType,
    handleChangeValue: handlers.handleChangeValue,
  })

  return {
    data: {
      methods,
      valueType,
      objectCode,
      code,
    },
    state: {
      isLoading,
      isFieldsLoading,
      watchIsAddFieldValue,
      watchBinding,
      ...state,
    },
    handlers,
    inputs,
  }
}
